import type { ReactNode } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';

export type StandardAuth0Config = {
    domain: string;
    clientID: string;
    audience: string;
    organization?: string;
    /** Surfaced to Auth0 actions as the `ext-env` authorization param — pass the app's build mode. */
    environmentName?: string;
};

/**
 * `<Auth0Provider>` with the settings every Wayste frontend shares: an offline_access scope so refresh tokens are
 * issued, localStorage caching so a hard refresh doesn't bounce the user, and the deep-link restore below.
 */
export const StandardAuth0Provider = ({ config, children }: { config: StandardAuth0Config; children: ReactNode }) => (
    <Auth0Provider
        domain={config.domain}
        clientId={config.clientID}
        authorizationParams={{
            audience: config.audience,
            scope: 'openid profile email offline_access',
            redirect_uri: window.location.origin,
            organization: config.organization,
            'ext-env': config.environmentName,
        }}
        cacheLocation="localstorage"
        onRedirectCallback={(appState) => {
            // Restore the deep link captured by Auth0LoginRedirect. replaceState alone only updates the address
            // bar — react-router syncs on popstate, so dispatch one or the rendered route stays out of sync with
            // the URL. Works for both the component and data routers.
            window.history.replaceState({}, '', appState?.returnTo || window.location.pathname);
            window.dispatchEvent(new PopStateEvent('popstate'));
        }}
        useRefreshTokens
    >
        {children}
    </Auth0Provider>
);
